import React, { useMemo } from "react";
import { Formik } from "formik";
import Box from "@mui/material/Box";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import DialogContent from "@mui/material/DialogContent";
import { useQuery } from "@apollo/client/react";
import CircularProgress from "@mui/material/CircularProgress";

import {
  fetchAssetValues,
  prepareAssetInputWithCustomFields,
} from "../../../../../../../utils/asset";
import {
  ASSET_QUERY,
  REMOVE_ASSET_MUTATION,
  UPDATE_ASSET_MUTATION,
  ADD_ASSET_IMAGE_MUTATION,
} from "../../../../../../../constants/graphql";
import {
  DELETE_INVENTORY_ITEM_MUTATION,
  UPDATE_INVENTORY_ITEM_MUTATION,
} from "../../../../../../../constants/graphql/mutations";
import { INVENTORY_REQUEST_QUERY } from "../../../../../../../constants/graphql/queries";

import useMutation from "../../../../../../../hooks/useMutation";
import ConfirmDeleteModal from "../../../../../../../components/images/ConfirmDeleteModal";
import useQueryParams from "../../../../../../../hooks/useQueryParams";
import { fullWidthDialog } from "../../../../../../../constants/dialog";
import { AssetsCollectionProvider } from "../../../../../../../contexts/assetsCollection";
import FormPage from "./FormPage";

export default function ItemDetails({
  id,
  project,
  inventoryRequest,
  inventoryItem,
  reFetch,
  onClose,
  onNextItem,
  sameLocation,
}) {
  const params = useQueryParams();
  const inventoryRequestId = params.get("id");
  const inventoryItemId = params.get("inventoryItemId");
  const bulkDuplicateModal = params.get("bulkDuplicateModal");
  const [openRemoveItem, setOpenRemoveItem] = React.useState(false);
  const { data: { asset = {} } = {}, loading } = useQuery(ASSET_QUERY, {
    variables: { id },
  });

  const [updateAsset, { loading: updatingAsset }] = useMutation(UPDATE_ASSET_MUTATION);
  const [createImage] = useMutation(ADD_ASSET_IMAGE_MUTATION);
  const [removeAsset, { loading: removingAsset }] = useMutation(REMOVE_ASSET_MUTATION, {
    refetchQueries: [{ query: INVENTORY_REQUEST_QUERY, variables: { id: inventoryRequestId } }],
  });
  const [removeInventoryItem, { loading: removingInventoryItem }] = useMutation(
    DELETE_INVENTORY_ITEM_MUTATION,
    {
      refetchQueries: [
        { query: INVENTORY_REQUEST_QUERY, variables: { id: inventoryRequestId } },
      ],
    }
  );
  const [updateInventoryItem] = useMutation(UPDATE_INVENTORY_ITEM_MUTATION, {
    refetchQueries: [{ query: INVENTORY_REQUEST_QUERY, variables: { id: inventoryRequestId } }],
  });

  const currentInventoryItem = inventoryRequest?.inventoryItems?.find(
    item => item.id === inventoryItemId
  );
  const inventoryItemQuantity = currentInventoryItem?.quantity || 0;
  const itemComment = currentInventoryItem?.comment;

  const initialValues = useMemo(
    () => ({
      ...fetchAssetValues(asset),
      availableQuantity: asset.availableQuantity,
      inventoryItemQuantity,
      itemComment,
    }),
    [asset, inventoryItemQuantity, itemComment]
  );

  const removing = removingAsset || removingInventoryItem;
  const loadingMutation = updatingAsset;

  const handleSave = async ({
    attachments,
    availableQuantity,
    inventoryItemQuantity,
    itemComment,
    ...values
  }) => {
    const rackLocationsChanges = params.get("rackLocationsChanges")
      ? JSON.parse(params.get("rackLocationsChanges"))
      : [];

    const inventoryItemInput = {
      quantity: inventoryItemQuantity,
      comment: itemComment,
      rackLocations: rackLocationsChanges.length
        ? rackLocationsChanges
        : inventoryItem.rackLocations,
    };

    const input = prepareAssetInputWithCustomFields(values, asset);

    await updateInventoryItem({
      variables: { id: inventoryItemId, input: inventoryItemInput },
    });
    const response = await updateAsset({ variables: { id, input } });
    if (bulkDuplicateModal !== "true") {
      onClose();
    }

    return response;
  };

  const handleRemoveAsset = async () => {
    if (project.importedAssets) {
      await removeInventoryItem({ variables: { id: inventoryItemId } });
    } else {
      await removeAsset({ variables: { id: asset.id } });
    }
    onClose();
  };

  const handleClose = () => {
    const currentParams = new URLSearchParams(window.location.search);
    const currentAddInventoryItem =
      currentParams.get("addInventoryItem") === "true";

    if (currentAddInventoryItem) {
      toggleRemoveItem();
    } else {
      onClose();
    }
  };

  const toggleRemoveItem = () => setOpenRemoveItem(prev => !prev);

  return (
    <Dialog open fullWidth maxWidth="xs" onClose={handleClose} sx={fullWidthDialog}>
      <DialogTitle sx={{ background: "#f4f6f8", display: "flex", justifyContent: "space-between" }}>
        <Box width="48px" />
        <Typography fontSize={24} fontWeight={500} color="textPrimary">
          Item Details
        </Typography>
        <IconButton onClick={handleClose}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ background: "#f4f6f8" }}>
        {loading ? (
          <Box height={258} display="flex" justifyContent="center" alignItems="center">
            <CircularProgress color="primary" />
          </Box>
        ) : (
          <Box>
            <AssetsCollectionProvider variables={{ projectId: project?.id }} skipQuery>
              <Formik key={asset?.id} initialValues={initialValues} onSubmit={handleSave}>
                <FormPage
                  type="outbound"
                  inventoryRequestId={inventoryRequest.id}
                  inventoryRequest={inventoryRequest}
                  currentAsset={asset}
                  onCloseModal={onClose}
                  handeRefetch={reFetch}
                  inventoryItem={inventoryItem}
                  removing={removing}
                  toggleRemoveItem={toggleRemoveItem}
                  sameLocation={sameLocation}
                  project={project}
                  closeItemDetails={handleClose}
                  loadingMutation={loadingMutation}
                  updateAsset={updateAsset}
                  createImage={createImage}
                  onNextItem={onNextItem}
                />
              </Formik>
            </AssetsCollectionProvider>
          </Box>
        )}
      </DialogContent>
      {openRemoveItem && (
        <ConfirmDeleteModal
          title="Delete Item"
          descr="Are you sure want to delete the selected item?"
          onClose={toggleRemoveItem}
          onDelete={handleRemoveAsset}
        />
      )}
    </Dialog>
  );
}
